import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import {
  FileService,
  ModuleState,
  NotificationActions,
  NotificationState,
  TypesUtilsService,
  UserUiSettings,
} from '@osos/shared/legacy-core';
import {
  DataModulePresenterModel,
  DEFAULT_PAGESIZE,
  NotificationPresenterModel,
} from '@osos/shared/types';
import { ComponentBase } from '@osos/shared/utility';
import { combineLatest, Observable } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-notification-menu',
  templateUrl: './notification-menu.component.html',
  styleUrls: ['./notification-menu.component.scss'],
  standalone: false,
})
export class NotificationMenuComponent extends ComponentBase implements OnInit {
  notifications: NotificationPresenterModel[] = [];
  totalCount = 0;
  loading = false;
  currentPage = 0;
  readNotification: string[] = [];
  @Output() notificationRedirect = new EventEmitter<boolean>();

  selectedModule$: Observable<DataModulePresenterModel>;
  notificationViewLink$: Observable<string>;

  constructor(
    private store: Store,
    private router: Router,
    private fileService: FileService,
    private typeUtils: TypesUtilsService,
    private _userUiSettings: UserUiSettings,
  ) {
    super();
    this.selectedModule$ = this.store.select(ModuleState.selectedModule);
    this.notificationViewLink$ = this.store.select(
      NotificationState.transitoryNotificationViewLink,
    );
  }

  ngOnInit(): void {
    this._fetchNotification(this.currentPage++, true);

    this._subscribeToNotificationChanges();

    this._subscribeToPageChanges();

    this._subscribeToNewNotificationChanges();
  }

  onScroll() {
    if (!this.loading && this.notifications.length < this.totalCount) {
      this.loading = true;
      this._fetchNotification(this.currentPage++, false);
    }
  }

  onNotificationClicked(notification: NotificationPresenterModel): void {
    const notIsNotReadAndNonexistentInListOfRead =
      !notification?.readAt && !this.readNotification.includes(notification.id);

    if (notIsNotReadAndNonexistentInListOfRead) {
      this.readNotification.push(notification.id);
      this.markNotificationAsRead(notification.id);
    }
    if (notification.attachment?.id) {
      this.fileService
        .getBlobByFileId(notification.attachment.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe((blob) => {
          this.typeUtils.downloadBlob(blob, notification.attachment.name);
        });
    } else {
      this.fetchNotificationView(notification);
    }
  }

  fetchNotificationView(notification: NotificationPresenterModel): void {
    this.store
      .dispatch(
        new NotificationActions.FetchTransitoryNotificationView({
          id: notification.id,
        }),
      )
      .pipe(
        switchMap(() =>
          this.store.select(NotificationState.transitoryNotificationViewLink),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe((link) => {
        if (link) {
          this.router.navigate([link]);
          this.notificationRedirect.emit(true);
        }
      });
  }

  markNotificationAsRead(notificationId: string): void {
    this.store.dispatch(
      new NotificationActions.MarkNotificationAsRead({
        id: notificationId,
      }),
    );
  }

  private _fetchNotification(
    skip: number,
    replace: boolean,
    limit?: number,
  ): Observable<void> {
    return this.store.dispatch(
      new NotificationActions.FetchNotification({
        skip: skip * DEFAULT_PAGESIZE,
        replace,
        limit,
      }),
    );
  }

  private _subscribeToNotificationChanges() {
    combineLatest([
      this.store.select(NotificationState.presentedNotifications),
      this.store.select(NotificationState.totalCount),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([notifications, count]) => {
        this.notifications = notifications;
        this.totalCount = count;
      });
  }

  private _subscribeToPageChanges(): void {
    this.store
      .select(NotificationState.loading('page'))
      .pipe(takeUntil(this.destroy$))
      .subscribe((loading) => {
        this.loading = loading;
      });
  }

  private _subscribeToNewNotificationChanges(): void {
    this.store
      .select(NotificationState.newNotification)
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        if (data) {
          this._fetchNotification(0, true, DEFAULT_PAGESIZE * this.currentPage);
        }
      });
  }
  get dateFormat(): string {
    return this._userUiSettings?.dateFormat ?? 'd MMM y';
  }
}
